import React, { useMemo } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Badge } from 'maeng-daisyui'
import classNames from 'classnames'
import Message, { useTranslation } from 'maeng-utils/locale'
import useGatsbyImage from '@/hooks/useGatsbyImage'
import { addClass } from '@/utils/dom'
import { INTERSECTION_OBSERVER_ROOT_MARGIN } from '@/constants'
import useIntersectionObserver from '@/hooks/useIntersectionObserver'

type ProjectItem = {
  link: string
  title: string
  term: string
  description: string
  attribution: string
  tags: string[]
  github: string
}

const Projects = () => {
  const { t } = useTranslation()

  useIntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      return entries
        .filter(({ isIntersecting }) => isIntersecting)
        .forEach(({ target }) => addClass(target as HTMLElement, 'observed-shown'))
    },
    { rootMargin: INTERSECTION_OBSERVER_ROOT_MARGIN },
  )

  const [maengDesign, seoul13, bmart] = useGatsbyImage([
    'maeng-design.png',
    'seoul13.png',
    'bmart.png',
  ])

  const projects = useMemo(() => {
    const projectList: ProjectItem[] = t('portfolio.projects.projects')
    return projectList.map((project, i) => {
      const { title, link, term, description, attribution, tags, github } = project
      return {
        image: [maengDesign, seoul13, bmart][i],
        link,
        details: {
          title,
          term,
          description,
          attribution,
          tags,
        },
        github,
      }
    })
  }, [bmart, maengDesign, seoul13, t])

  return (
    <section className="mb-12">
      <Message
        i18nKey="portfolio.projects.title"
        className="text-3xl font-extrabold pt-5 pb-8"
        component="h2"
      />
      <div className="flex flex-col gap-10">
        {projects.map(
          ({ link, image, details: { title, term, description, attribution, tags }, github }) => (
            <div
              className={classNames(
                'flex flex-col gap-4 justify-between',
                'observed observed-hidden',
                'md:flex-row',
              )}
              key={title}
            >
              <a
                className={classNames('flex flex-col grow-1 shrink-1 basis-[90%]', 'md:flex-row')}
                href={link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div
                  className={classNames(
                    'block items-center min-w-[4rem] max-w-[6rem] h-full',
                    'md:flex',
                  )}
                >
                  {image && (
                    <GatsbyImage
                      className="object-contain aspect-square bg-white rounded-xl shadow-lg border border-solid border-[#f0f0f0]"
                      image={image}
                      alt={title}
                    />
                  )}
                </div>
                <div className={classNames('ml-4 mt-4', 'md:mt-0')}>
                  <h5 className="text-lg flex items-center gap-1 mb-1">
                    <span className="font-semibold">{title}</span>
                    <span className="font-semibold text-xs text-[#8a8e94]">{term}</span>
                  </h5>
                  <div className="text-base mb-1">{description}</div>
                  <div className="text-sm text-[#767b83] mb-1">{attribution}</div>
                  <div className="flex flex-wrap gap-1">
                    {tags.map((tag) => (
                      <Badge size="sm" color="ghost" key={tag}>
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </a>
              <a
                href={github}
                className={classNames(
                  'flex items-center justify-center flex-basis basis-[5%] bg-[#242a2e] rounded-md cursor-pointer p-2',
                  'md:bg-transparent md:rounded-none md:p-0',
                )}
              >
                <div
                  className={classNames(
                    "w-6 h-6 bg-[url('../assets/images/github-white.png')] bg-contain bg-no-repeat",
                    "md:bg-[url('../assets/images/github.png')]",
                  )}
                />
              </a>
            </div>
          ),
        )}
      </div>
    </section>
  )
}

export default Projects
